import React, { useState } from 'react';
import { Waypoint } from 'react-waypoint';
import styled from 'styled-components';
import { formatMessage } from '@99designs/frontend-components/i18n';
import TrackScrollToPageElement from '../../../../shared/components/Analytics/TrackScrollToPageElement';
import Attribution from '../../../../shared/components/Attribution';
import Box from '../../../../shared/components/Box';
import DesignCard from '../../../../shared/components/DesignCard';
import Heading from '../../../../shared/components/Heading';
import Paragraph from '../../../../shared/components/Paragraph';
import Stars from '../../../../shared/components/Stars';
import { colors } from '../../../../shared/lib/styles';
import { CategoryType, EntryType } from '../ContestOverviewData';
import { MarkDesign, UnderlineText } from './DesignScribbles';

export interface Props {
    category: CategoryType;
    clientName: string;
    featureEntry: EntryType;
}

const FeedbackTextContainer = styled.span<{ active: boolean }>(({ active }) => {
    return `
        opacity: ${active ? 1 : 0};
        transition: 2s opacity;
        transition-delay: 1.5s;
    `;
});

export default function CollaborationSection({ category, clientName, featureEntry }: Props) {
    let scrollableAncestor;
    const [markDesignInView, setMarkDesignInView] = useState(false);
    const [underlineTextView, setUnderlineTextView] = useState(false);

    if (process.browser) {
        scrollableAncestor = window;
    }

    return (
        <>
            <TrackScrollToPageElement
                pageName="Contest Overview"
                pageElement="Collaboration Section"
            >
                <div className="row">
                    <div className="column small-12 medium-6 medium-offset-3 normal-5 normal-offset-6 large-4 large-offset-6">
                        <Box paddingBottom={['space10', 'space10', 'space15', 'space15']}>
                            <Heading type="h2" variations={['h1', 'h1-capped', 'no-margin']}>
                                {featureEntry
                                    ? formatMessage(
                                          __(
                                              '{clientName} collaborated with designers to refine their ideas'
                                          ),
                                          {
                                              clientName,
                                          }
                                      )
                                    : formatMessage(
                                          __(
                                              '{clientName} is collaborating with designers to refine their ideas'
                                          ),
                                          {
                                              clientName,
                                          }
                                      )}
                            </Heading>
                        </Box>
                    </div>
                </div>
            </TrackScrollToPageElement>

            <div className="row">
                <div className="column small-12 medium-6 medium-offset-3 normal-8 large-8 normal-push-4 large-push-4">
                    <div className="row">
                        <div className="column small-12 medium-12 normal-6 large-6">
                            {featureEntry ? (
                                <DesignCard
                                    src={featureEntry.designUrl}
                                    alt={formatMessage(
                                        __('{categoryTitle} contest entry by {designerName}'),
                                        {
                                            categoryTitle: category.title,
                                            designerName: featureEntry.designer.displayName,
                                        }
                                    )}
                                    variations={['lazyload', 'medium-shadow']}
                                    attribution={
                                        <Attribution name={featureEntry.designer.displayName} />
                                    }
                                />
                            ) : (
                                <DesignCard
                                    alt={__('contest entries coming soon')}
                                    variations={['lazyload', 'medium-shadow']}
                                />
                            )}
                        </div>
                        <div className="column small-12 medium-12 normal-6 large-6">
                            <Box
                                paddingTop={['space10', 'space10', 'space12', 'space12']}
                                paddingBottom={['space10', 'space10', 'space8', 'space8']}
                            >
                                <Stars rating={3} color={colors.grey800} animateOnScrollIntoView />
                            </Box>

                            <Heading type="h3" variations={['h5']}>
                                {__('Rate designs')}
                            </Heading>
                            <Paragraph>
                                {__(
                                    'When design entries come in, you can rate them so designers know what you’re looking for in your logo design.'
                                )}
                            </Paragraph>
                        </div>
                    </div>
                </div>
                <div className="column small-12 medium-6 medium-offset-3 normal-4 large-4 normal-pull-8 large-pull-8">
                    <Box paddingTop={['none', 'none', '260px', '340px']} />

                    <Heading type="h4" variations={['h5']} style={{ position: 'relative' }}>
                        <FeedbackTextContainer active={markDesignInView}>
                            {__('Give feedback')}
                        </FeedbackTextContainer>
                        <Waypoint
                            onEnter={() => setMarkDesignInView(true)}
                            topOffset={'-100px'}
                            bottomOffset={'-150px'}
                            scrollableAncestor={scrollableAncestor}
                        >
                            <MarkDesign active={markDesignInView} />
                        </Waypoint>
                    </Heading>

                    <Paragraph variations={['no-margin']}>
                        <FeedbackTextContainer active={markDesignInView}>
                            {__(
                                '99designs has great collaboration tools so you can pinpoint and capture your ideas'
                            )}
                        </FeedbackTextContainer>
                        <Waypoint
                            onEnter={() => setUnderlineTextView(true)}
                            topOffset={'-100px'}
                            bottomOffset={'-250px'}
                            scrollableAncestor={scrollableAncestor}
                        >
                            <UnderlineText active={underlineTextView} />
                        </Waypoint>
                    </Paragraph>
                </div>
            </div>
        </>
    );
}
